/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import {
  ArrowUpRight, Square, Type, Pencil, Eraser, Save, X, Camera, Loader2, MonitorSmartphone,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { tl } from '../../lib/testingLabApi';
import { toast } from 'sonner';

const COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#8b5cf6', '#000000', '#ffffff'];
const TOOLS = [
  { id: 'arrow', label: 'Arrow', icon: ArrowUpRight },
  { id: 'box', label: 'Box', icon: Square },
  { id: 'freehand', label: 'Pen', icon: Pencil },
  { id: 'text', label: 'Text', icon: Type },
];

/**
 * Captures a screenshot via getDisplayMedia, then opens an annotation canvas overlay.
 * Saves annotated image as evidence on the active session/step.
 */
export const ScreenCaptureButton = ({ sessionId, stepId, onCaptured, label = 'Capture screen', size = 'sm' }) => {
  const [busy, setBusy] = useState(false);
  const [captured, setCaptured] = useState(null); // dataURL of original

  const capture = async () => {
    setBusy(true);
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
      });
      const track = stream.getVideoTracks()[0];

      // Use ImageCapture if available, otherwise <video> + <canvas>
      let dataUrl;
      if (typeof window.ImageCapture === 'function') {
        const ic = new window.ImageCapture(track);
        const bitmap = await ic.grabFrame();
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        dataUrl = canvas.toDataURL('image/png');
      } else {
        const video = document.createElement('video');
        video.srcObject = stream;
        video.muted = true;
        await video.play();
        await new Promise((r) => setTimeout(r, 250)); // settle frame
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext('2d').drawImage(video, 0, 0);
        dataUrl = canvas.toDataURL('image/png');
      }

      track.stop();
      stream.getTracks().forEach((t) => t.stop());
      setCaptured(dataUrl);
    } catch (e) {
      toast.error(`Capture cancelled or failed: ${e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={capture} disabled={busy} data-testid="screen-capture-btn">
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MonitorSmartphone className="w-3 h-3 mr-1" />}
        {label}
      </Button>
      {captured && (
        <AnnotationModal
          imageDataUrl={captured}
          open={!!captured}
          onClose={() => setCaptured(null)}
          sessionId={sessionId}
          stepId={stepId}
          onSaved={(ev) => {
            setCaptured(null);
            onCaptured && onCaptured(ev);
          }}
        />
      )}
    </>
  );
};

/**
 * Annotation modal with arrow / box / pen / text tools over a captured image.
 * On save, exports flattened image and uploads as evidence.
 */
const AnnotationModal = ({ imageDataUrl, open, onClose, sessionId, stepId, onSaved }) => {
  const canvasRef = useRef(null);
  const overlayRef = useRef(null);  // separate canvas for committed strokes
  const [tool, setTool] = useState('arrow');
  const [color, setColor] = useState('#ef4444');
  const [strokes, setStrokes] = useState([]); // committed strokes
  const [drawing, setDrawing] = useState(null); // current draft
  const [busy, setBusy] = useState(false);
  const [textPrompt, setTextPrompt] = useState(null); // {x,y}
  const imgRef = useRef(null);
  const [imgSize, setImgSize] = useState({ w: 0, h: 0 });

  // Load image into canvas
  useEffect(() => {
    if (!open) return;
    const img = new Image();
    img.onload = () => {
      // Scale to fit, max 1280px wide for performance
      const maxW = 1280;
      const scale = img.width > maxW ? maxW / img.width : 1;
      const w = img.width * scale;
      const h = img.height * scale;
      setImgSize({ w, h });
      imgRef.current = img;
      requestAnimationFrame(() => redraw());
    };
    img.src = imageDataUrl;
  }, [imageDataUrl, open]);

  useEffect(() => { redraw(); }, [strokes, drawing]);

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas || !imgRef.current) return;
    const ctx = canvas.getContext('2d');
    canvas.width = imgSize.w;
    canvas.height = imgSize.h;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(imgRef.current, 0, 0, canvas.width, canvas.height);
    [...strokes, drawing].filter(Boolean).forEach((s) => drawStroke(ctx, s));
  };

  const drawStroke = (ctx, s) => {
    ctx.save();
    ctx.strokeStyle = s.color;
    ctx.fillStyle = s.color;
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    if (s.tool === 'arrow') {
      const { x1, y1, x2, y2 } = s;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      const angle = Math.atan2(y2 - y1, x2 - x1);
      const headlen = 14;
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - headlen * Math.cos(angle - Math.PI / 6), y2 - headlen * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(x2 - headlen * Math.cos(angle + Math.PI / 6), y2 - headlen * Math.sin(angle + Math.PI / 6));
      ctx.closePath(); ctx.fill();
    } else if (s.tool === 'box') {
      const { x1, y1, x2, y2 } = s;
      ctx.strokeRect(Math.min(x1, x2), Math.min(y1, y2), Math.abs(x2 - x1), Math.abs(y2 - y1));
    } else if (s.tool === 'freehand') {
      ctx.beginPath();
      (s.points || []).forEach((p, i) => i === 0 ? ctx.moveTo(p.x, p.y) : ctx.lineTo(p.x, p.y));
      ctx.stroke();
    } else if (s.tool === 'text') {
      ctx.font = 'bold 18px system-ui';
      ctx.fillStyle = s.color;
      ctx.strokeStyle = s.color === '#ffffff' ? 'black' : 'white';
      ctx.lineWidth = 3;
      ctx.strokeText(s.text || '', s.x, s.y);
      ctx.fillText(s.text || '', s.x, s.y);
    }
    ctx.restore();
  };

  const getCoords = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const sx = canvasRef.current.width / r.width;
    const sy = canvasRef.current.height / r.height;
    return { x: (e.clientX - r.left) * sx, y: (e.clientY - r.top) * sy };
  };

  const onMouseDown = (e) => {
    const p = getCoords(e);
    if (tool === 'text') { setTextPrompt(p); return; }
    if (tool === 'freehand') {
      setDrawing({ tool, color, points: [p] });
    } else {
      setDrawing({ tool, color, x1: p.x, y1: p.y, x2: p.x, y2: p.y });
    }
  };
  const onMouseMove = (e) => {
    if (!drawing) return;
    const p = getCoords(e);
    if (drawing.tool === 'freehand') {
      setDrawing({ ...drawing, points: [...drawing.points, p] });
    } else {
      setDrawing({ ...drawing, x2: p.x, y2: p.y });
    }
  };
  const onMouseUp = () => {
    if (drawing) {
      setStrokes((s) => [...s, drawing]);
      setDrawing(null);
    }
  };

  const undo = () => setStrokes((s) => s.slice(0, -1));
  const clear = () => setStrokes([]);

  const submitText = (text) => {
    if (text && textPrompt) {
      setStrokes((s) => [...s, { tool: 'text', color, x: textPrompt.x, y: textPrompt.y + 18, text }]);
    }
    setTextPrompt(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const blob = await new Promise((res) => canvasRef.current.toBlob(res, 'image/png'));
      const file = new File([blob], `annotated-${Date.now()}.png`, { type: 'image/png' });
      const ev = await tl.uploadEvidence(sessionId, file, {
        evidence_type: 'annotated_screenshot',
        step_id: stepId || null,
        title: 'Annotated screen capture',
      });
      toast.success('Annotated screenshot saved');
      onSaved && onSaved(ev);
    } catch (e) {
      toast.error(e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[95vh] flex flex-col" data-testid="annotation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Camera className="w-4 h-4" /> Annotate screenshot
            <span className="text-xs text-muted-foreground font-normal ml-auto">
              Tools: arrow / box / pen / text · undo / clear · save
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center gap-2 flex-wrap pb-2 border-b border-border">
          <div className="flex gap-1">
            {TOOLS.map((t) => (
              <Button key={t.id} size="sm" variant={tool === t.id ? 'default' : 'outline'}
                onClick={() => setTool(t.id)} className="h-8 text-xs"
                data-testid={`anno-tool-${t.id}`}>
                <t.icon className="w-3.5 h-3.5 mr-1" /> {t.label}
              </Button>
            ))}
          </div>
          <div className="flex gap-1 items-center px-2 border-l border-border">
            {COLORS.map((c) => (
              <button key={c} onClick={() => setColor(c)}
                className={cn('w-6 h-6 rounded-full border-2', color === c ? 'border-violet-500 scale-110' : 'border-border')}
                style={{ backgroundColor: c }}
                data-testid={`anno-color-${c.replace('#', '')}`} />
            ))}
          </div>
          <div className="flex gap-1 ml-auto">
            <Button size="sm" variant="outline" onClick={undo} disabled={!strokes.length} className="h-8 text-xs" data-testid="anno-undo">
              Undo
            </Button>
            <Button size="sm" variant="outline" onClick={clear} disabled={!strokes.length} className="h-8 text-xs" data-testid="anno-clear">
              <Eraser className="w-3 h-3 mr-1" /> Clear
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-auto bg-slate-100 dark:bg-slate-900 rounded">
          <div className="relative inline-block min-w-full p-2">
            <canvas
              ref={canvasRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              style={{ maxWidth: '100%', cursor: tool === 'text' ? 'text' : 'crosshair' }}
              className="border border-border bg-white"
              data-testid="anno-canvas"
            />
            {textPrompt && (
              <TextInputPopover x={textPrompt.x} y={textPrompt.y} onSubmit={submitText} onCancel={() => setTextPrompt(null)} />
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="anno-cancel">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button onClick={save} disabled={busy} data-testid="anno-save">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save annotated screenshot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TextInputPopover = ({ x, y, onSubmit, onCancel }) => {
  const [val, setVal] = useState('');
  return (
    <div className="absolute bg-card border border-violet-500 rounded shadow-lg p-1 flex gap-1"
      style={{ left: Math.min(x / 2, window.innerWidth - 200), top: Math.max(y / 2 - 20, 0) + 8 }}
      data-testid="anno-text-input">
      <input value={val} onChange={(e) => setVal(e.target.value)} autoFocus
        onKeyDown={(e) => { if (e.key === 'Enter') onSubmit(val); if (e.key === 'Escape') onCancel(); }}
        className="text-xs border-0 px-1 outline-none bg-transparent text-foreground" placeholder="Label…" />
      <Button size="sm" className="h-6 text-xs px-2" onClick={() => onSubmit(val)}>Add</Button>
    </div>
  );
};

export default ScreenCaptureButton;
